/* eslint-disable @typescript-eslint/naming-convention */
import _ from 'lodash';
import LogRocket from 'shared/logrocket';

import { persistCartData } from 'src/state/actions/cart';
import { getLogger } from 'src/utils/datadog-logger';

import validateSubmit from './validate-submit';
import validateCart from './validate-cart';
import buildOrder from './build-order';

const logger = getLogger('on-click-submit-v2');

const TIMEOUT_MS = 20000;
export const TIMEOUT_INTERNAL_MESSAGE = 'Order submission timed out after 20 seconds';

// called via use-kiosk-checkout-v2.tsx 264
// called via src/modals/kiosk-checkout/modal.jsx 90
export default async function onClickSubmitV2(
  submitOrder,
  {
    Cart,
    UI,
    User,
    apolloClient,
    showErnie,
    Checkout,
    checkOnDuty = false,
    isAnonymousCheckout = false,
    isEmailRequiredForCurbsideArrival = false,
    isExternalProviderEnabled = false,
    refetchRetailerPaymentData,
    tracker,
    flags,
    turnstileToken = '',
  }
): Promise<any> {
  const { isVariant, addErrors, saveMethods } = Checkout;

  // attempt to auto save specific sections
  try {
    // attempt to auto save sections that have registered themselves for saving
    await Promise.all(saveMethods.map((method) => method()));

    // old manually coded ones
    await Cart.medicalComponentRef?.current?.onSave();
    await Cart.orderTypeComponentRef?.current?.onSave();
    await Cart.scheduledOrderingRef?.current?.handleSave();
    // if we don't have a checkout token, persist cart to get one
    if (!Cart.checkoutToken) {
      await persistCartData(apolloClient, Cart, UI, User, flags);
    }
  } catch (e) {
    // log error to console--ernie logging is handled in respective onSave() calls above
    console.error(e);
    return { error: true, msg: e };
  }

  // validate the cart
  const { proceed: cartIsValid, outOfStockItem, returnToMenu } = await validateCart({
    Cart,
    UI,
    apolloClient,
    showErnie,
    isVariant,
    checkOnDuty,
  });
  if (!cartIsValid) {
    return { error: true, outOfStockItem, returnToMenu };
  }

  // validate the order
  const { proceed: orderIsValid } = await validateSubmit({
    Cart,
    UI,
    User,
    showErnie,
    Checkout,
    isEmailRequiredForCurbsideArrival,
    isExternalProviderEnabled,
  });

  if (!orderIsValid) {
    return { error: true };
  }
  // build the order
  const { order } = buildOrder({
    Cart,
    UI,
    User,
    Checkout,
    flags,
  });
  order.isAnonymous = isAnonymousCheckout;

  const abortController = new AbortController();

  // Create a timeout promise that rejects
  let timeoutId;
  const timeoutPromise = new Promise((resolve, reject) => {
    timeoutId = setTimeout(() => {
      logger.error(TIMEOUT_INTERNAL_MESSAGE, {
        dispensaryId: Cart.dispensary?.id,
        userId: User.id,
        checkoutToken: Cart.checkoutToken,
        uiVariant: UI.variant,
      });
      abortController.abort(); // Cancel the network request
      reject(new Error(TIMEOUT_INTERNAL_MESSAGE));
    }, TIMEOUT_MS);
  });

  // Submit order with abort signal
  const submitPromise = submitOrder({
    variables: { input: order, turnstileToken },
    context: {
      fetchOptions: {
        signal: abortController.signal,
      },
    },
  });

  try {
    // Race between the request and timeout
    const response = await Promise.race([submitPromise, timeoutPromise]);
    // Clear timeout since request completed successfully
    clearTimeout(timeoutId);

    const valid = _.get(response, 'data.createOrderV2.valid', false);
    const serverErrors = _.get(response, 'data.createOrderV2.errors', false);
    if (!valid && !_.isEmpty(serverErrors)) {
      const dutchiePayError = serverErrors[0].extensions?.path === 'dutchiePay';
      const reasonCode = serverErrors[0].extensions?.reason_code;
      const dutchiePayIsDown = serverErrors[0].extensions?.reason_code === 'S010';

      if (dutchiePayError) {
        tracker.paymentsError({ reasonCode: reasonCode ?? 'NO_REASON_CODE', meta: serverErrors[0]?.extensions });
      }

      if (dutchiePayError && dutchiePayIsDown) {
        refetchRetailerPaymentData();
      }

      addErrors(serverErrors);
      console.error({ 'Checkout Errors': serverErrors, Order: order });
    } else if (!valid) {
      console.error('CheckoutError', response);
      UI.showErnie('Error submitting order. Please contact customer support.');
      LogRocket.track('CheckoutError');
    }

    return response;
  } catch (error) {
    // Clear timeout in case of any error
    clearTimeout(timeoutId);

    // Handle timeout errors (from our timeout promise or AbortError)
    if (
      error.message === TIMEOUT_INTERNAL_MESSAGE ||
      error.name === 'AbortError' ||
      error.message.includes('aborted')
    ) {
      console.error('Order submission timeout:', error);
      UI.showErnie('Order submission timed out. Please try again.');
      LogRocket.track('CheckoutTimeout');
      return { error: true, msg: error };
    }
    throw error; // Re-throw other errors
  }
}
